/**
 * Job detail island loader: runs when we have static job shell (job-ssr) and job-app.
 * Mounts the interactive JobDetailPage into #job-app, swaps with #job-ssr, then
 * re-renders the tree so charts paint in the now-visible container.
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import { getReactRootSentryOptions } from '../../utils/react/react-root-sentry-options';
import type { Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { loadLocaleData } from '../../utils/routing/url-helpers';
import { buildFooterStaticStrings } from '../../utils/footer/buildFooterStaticStrings';
import { createStore } from '../../redux/createStore';
import { AuthProvider } from '../../contexts/AuthContext';
import { FeedbackModalProvider } from '../../contexts/FeedbackModalContext';
import { ToastProvider } from '../../contexts/ToastContext';
import { FooterStringsProvider } from '../../contexts/FooterStringsContext';
import { ToastContainer } from '../../components/Toast';
import { JobIslandProvider } from '../../contexts/JobIslandContext';
import { MinimalChrome } from '../../layouts/MinimalChrome';
import type { PageContextClient } from 'vike/types';
import type { Data } from './@jobId/@seoSlug/+data';
import { JOB_ISLAND_SWAPPED_EVENT } from './job-island-events';

/** Delay before swap; then we re-render again after swap so charts paint when visible. */
const SWAP_DELAY_MS = 100;

let jobRoot: Root | null = null;
let jobRootContainer: HTMLElement | null = null;

function getLocaleFromPathname(pathname: string): string {
  const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return first && ['en', 'fi', 'sv'].includes(first) ? first : 'en';
}

function domNodeHasReactFiber(el: Element): boolean {
  const keys = Object.getOwnPropertyNames(el);
  return keys.some((k) => k.startsWith('__reactFiber') || k.startsWith('__reactContainer'));
}

/**
 * First visit to a job URL: `onRenderClient` skips `createRoot`/`hydrateRoot` on `#root`, so the shell is static HTML.
 * Client navigation to a job: Vike renders Layout into `#root` — React must remove `#job-ssr` via state, not raw `remove()`.
 */
function isJobLayoutReactManaged(): boolean {
  if (typeof window === 'undefined') return false;
  if ((window as unknown as { __VIKE_ROOT__?: unknown }).__VIKE_ROOT__) return true;
  const root = document.getElementById('root');
  if (!root) return false;
  if (domNodeHasReactFiber(root)) return true;
  const first = root.firstElementChild;
  return first ? domNodeHasReactFiber(first) : false;
}

function runSwap(ssr: HTMLElement, app: HTMLElement): void {
  const wrapper = app.parentElement;
  if (wrapper) {
    wrapper.style.minHeight = `${wrapper.offsetHeight}px`;
  }

  const reactManaged = isJobLayoutReactManaged();

  if (reactManaged) {
    ssr.style.display = 'none';
    ssr.style.visibility = 'hidden';
    ssr.setAttribute('aria-hidden', 'true');
    ssr.classList.add('job-ssr--replaced');
  } else {
    // Static SSR only: drop the duplicate subtree (display:none still left two full trees in the DOM).
    ssr.remove();
  }

  app.style.visibility = 'visible';
  app.style.opacity = '1';
  app.style.pointerEvents = 'auto';
  app.removeAttribute('aria-hidden');
  app.style.position = 'static';

  window.dispatchEvent(new CustomEvent(JOB_ISLAND_SWAPPED_EVENT, { bubbles: true }));

  if (wrapper) {
    requestAnimationFrame(() => {
      wrapper.style.minHeight = '';
    });
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('resize'));
  }
}

/** Parsed payload: either legacy (data only) or { data, userState } for island hydration. */
interface JobInitialPayload {
  data?: Data;
  userState?: Record<string, unknown>;
  job?: unknown;
}

function parseJobInitial(scriptEl: HTMLScriptElement): { jobData: Data | null; userState: Record<string, unknown> } {
  try {
    const raw = scriptEl.textContent ?? '';
    const parsed = JSON.parse(raw) as JobInitialPayload;
    if (!parsed || typeof parsed !== 'object') {
      return { jobData: null, userState: {} };
    }
    // New shape: { data, userState }
    if (parsed.data && typeof parsed.data === 'object') {
      const data = parsed.data as Data;
      return {
        jobData: data,
        userState: (parsed.userState && typeof parsed.userState === 'object' ? parsed.userState : {}) as Record<string, unknown>,
      };
    }
    // Legacy shape: raw job data (data at top level with .job)
    const asData = parsed as unknown as Data;
    if (asData?.job) {
      return { jobData: asData, userState: {} };
    }
    return { jobData: null, userState: {} };
  } catch {
    return { jobData: null, userState: {} };
  }
}

export function runJobIslandLoader(
  pageContext: PageContextClient,
  payloadOverride?: { jobData?: Data | null; userState?: Record<string, unknown> },
): void {
  const appEl = document.getElementById('job-app');
  const scriptEl = document.getElementById('job-initial');
  if (!appEl) return;

  const parsedFromScript =
    scriptEl && scriptEl.tagName === 'SCRIPT'
      ? parseJobInitial(scriptEl as HTMLScriptElement)
      : { jobData: null, userState: {} };
  const jobData = payloadOverride?.jobData ?? parsedFromScript.jobData;
  const userState = payloadOverride?.userState ?? parsedFromScript.userState;
  console.log('[SimilarJobsDebug][job.client] loader-input', {
    objectId: (jobData as { job?: { objectID?: string } } | null)?.job?.objectID ?? 'none',
    overrideUsed: !!payloadOverride?.jobData,
    overrideSimilarCount: Array.isArray(payloadOverride?.jobData?.similarJobs) ? payloadOverride?.jobData?.similarJobs?.length : null,
    parsedSimilarCount: Array.isArray(parsedFromScript.jobData?.similarJobs) ? parsedFromScript.jobData?.similarJobs?.length : null,
    finalSimilarCount: Array.isArray(jobData?.similarJobs) ? jobData.similarJobs.length : -1,
  });
  if (!jobData?.job) {
    appEl.style.visibility = 'visible';
    appEl.style.pointerEvents = 'auto';
    return;
  }

  const pc = pageContext as { userState?: Record<string, unknown> } | undefined;
  const effectiveUserState = Object.keys(userState).length > 0 ? userState : (pc?.userState && typeof pc.userState === 'object' ? pc.userState : {});

  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : ((pageContext as any)?.urlPathname ?? '/');
  const locale = (pageContext?.locale as string) || getLocaleFromPathname(pathname) || 'en';
  const urlOriginal = typeof window !== 'undefined' ? window.location.href : pathname;
  const messages = loadLocaleData(locale);
  const footerStrings = buildFooterStaticStrings(messages);
  const store = createStore({
    ...pageContext,
    data: jobData,
    userState: effectiveUserState,
    _skipPersist: true,
  } as PageContextClient);
  const jobIslandValue = {
    locale,
    pathname,
    urlOriginal,
    user: (pageContext?.user ?? null) as any,
  };

  // Hidden until swap so we don't paint into a hidden tree; we re-render after swap so charts paint when visible
  appEl.style.visibility = 'visible';
  appEl.style.opacity = '0';
  appEl.style.pointerEvents = 'none';

  const mount = async (): Promise<void> => {
    const { default: JobDetailPage } = await import('./@jobId/@seoSlug/+Page');
    const buildTree = (contentKey: string) =>
      React.createElement(
        Provider,
        { store },
        React.createElement(
          IntlProvider,
          { messages, locale, defaultLocale: 'en' },
          React.createElement(
            FooterStringsProvider,
            { strings: footerStrings },
            React.createElement(
              AuthProvider,
              { initialUser: (pageContext?.user ?? null) as any },
              React.createElement(
                FeedbackModalProvider,
                null,
                React.createElement(
                  ToastProvider,
                  null,
                  React.createElement(React.Fragment, null,
                    React.createElement(ToastContainer),
                    React.createElement(
                      JobIslandProvider,
                      { value: jobIslandValue },
                      React.createElement(
                        MinimalChrome,
                        null,
                        React.createElement(JobDetailPage, {
                          key: contentKey,
                          initialData: jobData,
                          initialLocale: locale,
                        })
                      )
                    )
                  )
                )
              )
            )
          )
        )
      );
    const tree = buildTree('hidden');

    if (jobRoot && jobRootContainer === appEl) {
      jobRoot.render(tree);
    } else {
      if (jobRoot && jobRootContainer) {
        jobRoot.unmount();
        jobRoot = null;
        jobRootContainer = null;
      }
      jobRoot = createRoot(appEl, getReactRootSentryOptions());
      jobRootContainer = appEl;
      jobRoot.render(tree);
    }

    // Re-render with key "visible" so charts remount and paint in the container, then swap.
    // If we swap before this remount, React unmounts key="hidden" first and users see a blank
    // #job-app for a frame between unmount and mount.
    setTimeout(() => {
      const ssr = document.getElementById('job-ssr');
      const revealApp = (): void => {
        if (ssr) {
          runSwap(ssr, appEl);
        } else {
          appEl.style.visibility = 'visible';
          appEl.style.opacity = '1';
          appEl.style.pointerEvents = 'auto';
          appEl.removeAttribute('aria-hidden');
          appEl.style.position = 'static';
        }
      };
      const doSecondRender = (): void => {
        if (jobRoot && jobRootContainer === appEl) {
          jobRoot.render(buildTree('visible'));
        }
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          doSecondRender();
          requestAnimationFrame(() => {
            requestAnimationFrame(revealApp);
          });
        });
      });
    }, SWAP_DELAY_MS);
  };

  void mount();
}
